// ===================================================
// ФАЙЛ: frontend/src/components/Layout/Header.jsx
// Header: поиск, компания, баланс(₽), AI помощник, уведомления, профиль
// ✅ ОБНОВЛЕНО: Расширенная строка поиска, улучшенная структура
// ===================================================
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { ScreenLockContext } from '../../contexts/ScreenLockContext';
import {
  AlertTriangle,
  CheckCircle,
  Menu,
  Search,
  RefreshCw,
  HelpCircle,
  Lock,
  LayoutGrid,
  X,
  PanelRight,
} from 'lucide-react';
import MbTooltip from '../ui/MbTooltip';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import axios from 'utils/axios';
import { BetaBadge } from '../Common/VersionBadge';
import WayGptIcon from '../Common/WayGptIcon';
import { useWaySenCompanionOptional } from '../../contexts/WaySenCompanionContext';
import NotificationBell, { NOTIFICATIONS_UNREAD_EVENT } from '../Notifications/NotificationBell';
import notificationService from '../../services/notificationService';
import ImportMonitorDrawer, { OPEN_PROCESS_MONITOR_EVENT } from '../Sync/ImportMonitorDrawer';
import { useBilling } from '../../contexts/BillingContext';
import { useTariffName } from '../../hooks/useTariffName';
import CompanySwitcher from './CompanySwitcher';
import { SIDEBAR_MOBILE_OPEN_EVENT } from './Sidebar';
import { HELPER_SIDEBAR_TOGGLE_EVENT } from '../HelperSidebar/events';
import { GLOBAL_SEARCH_EVENT } from '../GlobalSearch/GlobalSearch';
import ProfileDropdown from './ProfileDropdown';
import PlatformModuleIcon from '../Common/PlatformModuleIcon';
import {
  PLATFORM_ASSET_LABELS,
  getPlatformAssetKeyForPath,
} from '../../utils/platformModuleRouteMap';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../../utils/safeRussianBackendMessage';
import { HELP_URL } from '../../utils/externalLinks';
import { toastAPI } from '../../contexts/ToastContext';
import {
  GLOBAL_SEARCH_ACTIVATION,
  GLOBAL_SEARCH_ACTIVATION_EVENT,
  getGlobalSearchActivationMode,
  setGlobalSearchActivationMode,
} from '../../utils/globalSearchActivation';
import { resolveHeaderProcessBadge } from '../../utils/processMonitorDisplay';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, logout, checkAuth } = useAuth();
  const { balance: billingBalance } = useBilling();
  const { isEnabled: screenLockEnabled } = useContext(ScreenLockContext);
  const companion = useWaySenCompanionOptional();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [togglingCompany, setTogglingCompany] = useState(false);
  /** Подтверждение вкл/выкл компании (вместо AntD Modal.confirm) */
  const [companyTogglePending, setCompanyTogglePending] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchActivationMode, setSearchActivationMode] = useState(() => getGlobalSearchActivationMode());
  const [activeProcessCount, setActiveProcessCount] = useState(0);
  const [hasRunningProcesses, setHasRunningProcesses] = useState(false);
  const [processBadge, setProcessBadge] = useState({
    count: 0,
    text: null,
    kind: 'empty',
    tooltip: 'Процессы'
  });
  const [notifUnread, setNotifUnread] = useState(0);
  /** Чтобы не спамить toast при опросе каждые 10 с — одно предупреждение на «полосу» сбоев. */
  const processesPollOkRef = useRef(true);

  const { tariffName } = useTariffName(company);

  const headerModuleKey = useMemo(
    () => getPlatformAssetKeyForPath(location.pathname),
    [location.pathname]
  );

  // savedAccounts больше не нужны здесь — ProfileDropdown загружает сам

  // Адаптивный режим для скрытия переключателя компании в header на мобильных
  useEffect(() => {
    const syncIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    syncIsMobile();
    window.addEventListener('resize', syncIsMobile);
    return () => window.removeEventListener('resize', syncIsMobile);
  }, []);

  useEffect(() => {
    const onActivationChange = (e) => {
      const next = e?.detail?.mode;
      if (next === GLOBAL_SEARCH_ACTIVATION.HOTKEY || next === GLOBAL_SEARCH_ACTIVATION.TYPE) {
        setSearchActivationMode(next);
      } else {
        setSearchActivationMode(getGlobalSearchActivationMode());
      }
    };
    window.addEventListener(GLOBAL_SEARCH_ACTIVATION_EVENT, onActivationChange);
    return () => window.removeEventListener(GLOBAL_SEARCH_ACTIVATION_EVENT, onActivationChange);
  }, []);

  useEffect(() => {
    const onOpen = () => setDrawerOpen(true);
    window.addEventListener(OPEN_PROCESS_MONITOR_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PROCESS_MONITOR_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const onUnread = (e) => {
      const count = e?.detail?.count;
      if (typeof count === 'number') setNotifUnread(count);
    };
    window.addEventListener(NOTIFICATIONS_UNREAD_EVENT, onUnread);
    return () => window.removeEventListener(NOTIFICATIONS_UNREAD_EVENT, onUnread);
  }, []);

  // Мобильный: бейдж на переключателе правой панели (HelperSidebar lazy — свой poll)
  useEffect(() => {
    if (!isMobile) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await notificationService.getUnreadCount();
        if (!cancelled) setNotifUnread(data.count ?? 0);
      } catch {
        /* не критично для бейджа */
      }
    };
    load();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load();
    }, 30000);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isMobile]);

  const setSearchMode = (mode) => {
    setSearchActivationMode(setGlobalSearchActivationMode(mode));
  };

  // Периодическая проверка процессов для кнопки "Процессы".
  // Adaptive polling: чаще при наличии running процессов, реже когда тихо,
  // полная пауза при скрытой вкладке. Цель: не дёргать API каждые 10 секунд
  // у пользователей без активных задач — это типичный случай и был источник
  // фоновой нагрузки на backend (HEADER_PROCESSES_POLL_HOT_MS / _IDLE_MS).
  useEffect(() => {
    let interval;
    let currentDelay = 0;
    let hasRunningLocal = false;

    const computeDelay = () => (hasRunningLocal ? 10_000 : 60_000);

    const load = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const resp = await axios.get('/api/user-resources/processes/active');
        const d = resp.data;
        if (d?.success) {
          processesPollOkRef.current = true;
          const procs = d.data || [];
          const badge = resolveHeaderProcessBadge(procs);
          const running = procs.some(p => p.status === 'running');
          hasRunningLocal = running;
          setActiveProcessCount(prev => (prev !== badge.count ? badge.count : prev));
          setHasRunningProcesses(running);
          setProcessBadge((prev) => (
            prev.kind === badge.kind && prev.text === badge.text && prev.tooltip === badge.tooltip
              ? prev
              : badge
          ));
          return;
        }
        hasRunningLocal = false;
        setActiveProcessCount(0);
        setHasRunningProcesses(false);
        setProcessBadge({ count: 0, text: null, kind: 'empty', tooltip: 'Процессы' });
        if (processesPollOkRef.current) {
          processesPollOkRef.current = false;
          toastAPI?.warning?.(
            pickSafeRussianBackendMessage(
              d?.error ?? d?.message,
              'Не удалось обновить список процессов.'
            )
          );
        }
      } catch (e) {
        hasRunningLocal = false;
        setActiveProcessCount(0);
        setHasRunningProcesses(false);
        setProcessBadge({ count: 0, text: null, kind: 'empty', tooltip: 'Процессы' });
        if (processesPollOkRef.current) {
          processesPollOkRef.current = false;
          toastAPI?.warning?.(
            pickSafeRussianBackendMessage(
              e?.response?.data?.error ?? e?.response?.data?.message,
              pickHttpStatusRussianFallback(e?.response?.status)
            )
          );
        }
      } finally {
        const next = computeDelay();
        if (next !== currentDelay) {
          currentDelay = next;
          if (interval) clearInterval(interval);
          interval = setInterval(load, currentDelay);
        }
      }
    };

    load();
    currentDelay = computeDelay();
    interval = setInterval(load, currentDelay);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);



  // Баланс
  const getBalanceValue = () => {
    const v = company?.balance !== undefined ? company.balance : billingBalance?.balance;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const balance = getBalanceValue();

  const runCompanyToggle = async (checked) => {
    const action = checked ? 'activate' : 'deactivate';
    try {
      setTogglingCompany(true);
      const resp = await axios.put(`/api/settings/${action}-company`);
      if (resp.data.success) {
        toastAPI?.success?.(resp.data.message);
        await checkAuth();
        window.location.reload();
      } else {
        toastAPI?.error?.(
          pickSafeRussianBackendMessage(
            resp.data?.error ?? resp.data?.message,
            pickHttpStatusRussianFallback(resp.status)
          )
        );
      }
    } catch (error) {
      toastAPI?.error?.(
        pickSafeRussianBackendMessage(
          error.response?.data?.error ?? error.response?.data?.message,
          pickHttpStatusRussianFallback(error.response?.status)
        )
      );
    } finally {
      setTogglingCompany(false);
      setCompanyTogglePending(null);
    }
  };

  const handleToggleCompany = (checked) => {
    setCompanyTogglePending({ activate: checked });
  };

  useEffect(() => {
    if (!companyTogglePending) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setCompanyTogglePending(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [companyTogglePending]);

  const openCompanionHelp = () => {
    if (companion?.openWindow) {
      companion.openPanel?.('talk', true);
      return;
    }
    if (companion?.openPanel) {
      companion.openPanel('talk');
      return;
    }
    navigate('/chat');
  };

  const openCompanionContext = () => {
    if (companion?.openPanel) {
      companion.openPanel('guide');
      return;
    }
    window.open(HELP_URL, '_blank', 'noopener,noreferrer');
  };

  // Переключение аккаунтов и добавление — всё внутри ProfileDropdown

  // buildUserMenuItems удалён — используется единый ProfileDropdown

  // Открытие мобильного sidebar
  const openMobileSidebar = () => {
    window.dispatchEvent(new CustomEvent(SIDEBAR_MOBILE_OPEN_EVENT));
  };

  const toggleHelperSidebar = () => {
    window.dispatchEvent(new CustomEvent(HELPER_SIDEBAR_TOGGLE_EVENT));
  };

  // Открытие глобального поиска
  const openSearch = () => {
    window.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_EVENT));
  };

  return (
    <>
      <header className="mb-header mb-glass-panel">
        {/* ── Левая часть: гамбургер + компания ── */}
        <div className="mb-header__left">
          <button type="button" className="mb-sidebar-mobile-trigger" onClick={openMobileSidebar} aria-label="Открыть меню">
            <Menu size={20} />
          </button>
          <CompanySwitcher placement="bottomLeft" />
          <BetaBadge className={`mb-header__beta-badge${isMobile ? ' mb-header__beta-badge--mobile' : ''}`} />
          {headerModuleKey && (
            <MbTooltip title={PLATFORM_ASSET_LABELS[headerModuleKey] || headerModuleKey} placement="bottom">
              <div
                className="mb-header__module-chip"
                aria-label={PLATFORM_ASSET_LABELS[headerModuleKey] || headerModuleKey}
              >
                <PlatformModuleIcon
                  assetKey={headerModuleKey}
                  fallback={<LayoutGrid size={18} />}
                  width={22}
                  height={22}
                  imgClassName="mb-header__module-asset-img"
                />
                <span className="mb-header__module-chip-label">
                  {PLATFORM_ASSET_LABELS[headerModuleKey] || headerModuleKey}
                </span>
              </div>
            </MbTooltip>
          )}
        </div>

        {/* ── Центр: поисковая строка + режим открытия ── */}
        <div
          className="mb-header__search-bar"
          role="search"
          data-activation-mode={searchActivationMode}
        >
          <button
            type="button"
            className="mb-header__search-main"
            onClick={openSearch}
            aria-label="Открыть глобальный поиск"
          >
            <Search size={18} className="mb-header__search-icon" aria-hidden />
            <span className="mb-header__search-text mb-header__search-text--desktop">
              {searchActivationMode === GLOBAL_SEARCH_ACTIVATION.HOTKEY
                ? 'Поиск — откройте Ctrl+K или кликом'
                : 'Поиск товаров, штрихкодов — начните ввод'}
            </span>
            <span className="mb-header__search-text mb-header__search-text--mobile">Поиск</span>
          </button>
          <div
            className="mb-header__search-mode"
            role="group"
            aria-label="Как открывать поиск"
          >
            <MbTooltip
              title="Открывать поиск только по Ctrl+K (или кликом по строке). Ввод с клавиатуры вне полей не откроет поиск."
              placement="bottom"
            >
              <button
                type="button"
                className={`mb-header__search-mode-btn${searchActivationMode === GLOBAL_SEARCH_ACTIVATION.HOTKEY ? ' is-active' : ''}`}
                aria-pressed={searchActivationMode === GLOBAL_SEARCH_ACTIVATION.HOTKEY}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearchMode(GLOBAL_SEARCH_ACTIVATION.HOTKEY);
                }}
              >
                Ctrl+K
              </button>
            </MbTooltip>
            <MbTooltip
              title="Поиск открывается, как только начинаете печатать (и для сканера штрихкодов). Удобно на складе."
              placement="bottom"
            >
              <button
                type="button"
                className={`mb-header__search-mode-btn${searchActivationMode === GLOBAL_SEARCH_ACTIVATION.TYPE ? ' is-active' : ''}`}
                aria-pressed={searchActivationMode === GLOBAL_SEARCH_ACTIVATION.TYPE}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearchMode(GLOBAL_SEARCH_ACTIVATION.TYPE);
                }}
              >
                Ввод
              </button>
            </MbTooltip>
          </div>
        </div>

        {/* ── Правая часть: действия ── */}
        <div className="mb-header__right">
          {/* WaySen / AI Помощник — переносимое окно companion */}
          <MbTooltip title="Помощник WaySen" placement="bottom">
            <button
              type="button"
              className="mb-header__icon-btn mb-header__ai-btn"
              onClick={openCompanionHelp}
              aria-label="Помощник WaySen"
            >
              <WayGptIcon size={20} variant="chip" aria-hidden />
            </button>
          </MbTooltip>

          {/* Контекст страницы */}
          <MbTooltip title="Что это за раздел?" placement="bottom">
            <button
              type="button"
              className="mb-header__icon-btn mb-header__help-btn"
              onClick={openCompanionContext}
              aria-label="Справка по разделу"
            >
              <HelpCircle size={20} aria-hidden />
            </button>
          </MbTooltip>

          {/* Процессы: U9 бейдж = catalog % того же running harvest, что drawer */}
          <MbTooltip title={processBadge.tooltip || 'Процессы'} placement="bottom">
            <button
              type="button"
              className={`mb-header__icon-btn mb-header__process-btn${activeProcessCount > 0 ? ' mb-header__icon-btn--active' : ''}`}
              onClick={() => setDrawerOpen(true)}
              aria-label={processBadge.kind === 'percent' ? processBadge.tooltip : 'Процессы'}
            >
              <span className="mb-header__process-hit">
                <RefreshCw size={18} className={hasRunningProcesses ? 'mb-icon-spin' : ''} aria-hidden />
                {processBadge.text ? (
                  <span
                    className={`mb-header__process-count${hasRunningProcesses ? ' mb-header__process-count--running' : ''}${processBadge.kind === 'percent' ? ' mb-header__process-count--percent' : ''}`}
                  >
                    {processBadge.text}
                  </span>
                ) : null}
              </span>
            </button>
          </MbTooltip>

          {/* Статус компании — скрывается раньше профиля (см. header.css progressive) */}
          {company && (
            <MbTooltip
              title={company?.is_active !== false ? 'Компания включена' : 'Компания отключена'}
              placement="bottom"
            >
              <input
                type="checkbox"
                role="switch"
                aria-checked={company?.is_active !== false}
                className="mb-switch mb-header__company-switch"
                checked={company?.is_active !== false}
                disabled={togglingCompany}
                onChange={(e) => handleToggleCompany(e.target.checked)}
              />
            </MbTooltip>
          )}

          {/* Уведомления: десктоп в шапке; мобильный — внутри правого сайдбара */}
          {!isMobile ? <NotificationBell /> : null}

          {/* Переключатель правого сайдбара (мобильный) */}
          <MbTooltip title="Панель помощника и уведомлений" placement="bottom">
            <button
              type="button"
              className="mb-header__icon-btn mb-header__helper-toggle"
              onClick={toggleHelperSidebar}
              aria-label={
                notifUnread > 0
                  ? `Правая панель, непрочитанных уведомлений: ${notifUnread}`
                  : 'Открыть правую панель'
              }
            >
              <PanelRight size={18} aria-hidden />
              {notifUnread > 0 ? (
                <span className="mb-header__helper-toggle-count">
                  {notifUnread > 99 ? '99+' : notifUnread}
                </span>
              ) : null}
            </button>
          </MbTooltip>

          {/* Блокировка экрана — только если включена в настройках */}
          {screenLockEnabled && (
            <MbTooltip title="Заблокировать сеанс" placement="bottom">
              <button
                type="button"
                className="mb-header__icon-btn"
                onClick={() => window.dispatchEvent(new Event('markbase_screen_lock'))}
                aria-label="Заблокировать сеанс"
              >
                <Lock size={18} aria-hidden />
              </button>
            </MbTooltip>
          )}

          {/* Профиль — единый ProfileDropdown */}
          <ProfileDropdown
            user={user}
            onLogout={logout}
            balance={balance}
            tariffName={tariffName}
            moduleSettingsUrl="/settings"
            navigate={navigate}
          />
        </div>
      </header>
      <ImportMonitorDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {companyTogglePending ? (
        <>
          <div
            className="mb-modal-backdrop"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setCompanyTogglePending(null); }}
            role="presentation"
            aria-hidden="true"
          />
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 'calc(var(--z-modal) + 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              pointerEvents: 'none',
            }}
          >
            <div
              className="mb-modal mb-modal--sm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mb-company-toggle-title"
              style={{ pointerEvents: 'auto', width: 'min(420px, calc(100vw - 32px))' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="mb-modal__header">
                <h2 id="mb-company-toggle-title" className="mb-modal__title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {companyTogglePending.activate ? (
                    <CheckCircle size={22} className="mb-icon-success" aria-hidden style={{ color: 'var(--ok)' }} />
                  ) : (
                    <AlertTriangle size={22} aria-hidden style={{ color: 'var(--warn)' }} />
                  )}
                  {companyTogglePending.activate ? 'Включить компанию?' : 'Отключить компанию?'}
                </h2>
                <button
                  type="button"
                  className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                  onClick={() => setCompanyTogglePending(null)}
                  aria-label="Закрыть"
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
              <div className="mb-modal__content">
                <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 'var(--font-body-sm)', lineHeight: 1.5 }}>
                  {companyTogglePending.activate
                    ? 'Все функции будут восстановлены. Возобновится списание средств.'
                    : 'Доступ к платным функциям будет ограничен. Списание прекратится.'}
                </p>
              </div>
              <div className="mb-modal__footer">
                <button type="button" className="mb-btn mb-btn--outline mb-btn--sm" onClick={() => setCompanyTogglePending(null)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className={`mb-btn mb-btn--sm ${companyTogglePending.activate ? 'mb-btn--primary' : 'mb-btn--danger'}`}
                  onClick={() => void runCompanyToggle(companyTogglePending.activate)}
                  disabled={togglingCompany}
                >
                  {togglingCompany ? <span className="mb-spin mb-spin--sm" aria-hidden /> : null}
                  {companyTogglePending.activate ? 'Включить' : 'Отключить'}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default Header;
